import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_INITIAL = 36;
const DEFAULT_STEP = 24;

/**
 * Progressive window for large card grids — mounts only the first `initial`
 * items, then appends more as the sentinel scrolls into view. Avoids dumping
 * thousands of DOM nodes on first paint (Garment Designs / catalogues).
 */
export default function ProgressiveGrid({
  items,
  renderItem,
  getItemKey,
  className = '',
  initial = DEFAULT_INITIAL,
  step = DEFAULT_STEP,
  empty = null,
}) {
  const list = Array.isArray(items) ? items : [];
  const [visibleCount, setVisibleCount] = useState(() => Math.min(initial, list.length));
  const sentinelRef = useRef(null);
  const listIdentity = useMemo(() => {
    if (list.length === 0) return 'empty';
    const first = getItemKey ? getItemKey(list[0], 0) : list[0]?._id || list[0]?.id || 0;
    const last = getItemKey
      ? getItemKey(list[list.length - 1], list.length - 1)
      : list[list.length - 1]?._id || list[list.length - 1]?.id || list.length;
    return `${list.length}:${first}:${last}`;
  }, [list, getItemKey]);

  useEffect(() => {
    setVisibleCount(Math.min(initial, list.length));
  }, [listIdentity, initial, list.length]);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(list.length, prev + step));
  }, [list.length, step]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || visibleCount >= list.length) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { root: null, rootMargin: '400px 0px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore, visibleCount, list.length]);

  if (list.length === 0) return empty;

  const visible = list.slice(0, visibleCount);

  return (
    <>
      <div className={className}>
        {visible.map((item, index) => {
          const key = getItemKey ? getItemKey(item, index) : item?._id || item?.id || index;
          return (
            <div key={key} style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 280px' }}>
              {renderItem(item, index)}
            </div>
          );
        })}
      </div>
      {visibleCount < list.length ? (
        <div ref={sentinelRef} className="flex justify-center py-6 text-sm text-gray-500" aria-hidden>
          Showing {visibleCount} of {list.length}…
        </div>
      ) : null}
    </>
  );
}
